import { type ReactNode, Suspense, useMemo } from 'react';
import { RouterProvider } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig, isAuthDisabled, authService } from '@/auth';
import { buildRouter } from './config/router';

/**
 * MSAL instance - created once and shared across the app.
 * Only created if auth is enabled.
 */
export const msalInstance = isAuthDisabled ? null : new PublicClientApplication(msalConfig);

// Initialize the auth service with the MSAL instance (if enabled)
if (msalInstance) {
    authService.initialize(msalInstance);
    console.log('[Portal] Auth service initialized');
} else {
    console.log('[Portal] Auth is disabled via configuration');
}

/**
 * Loading fallback for Suspense boundaries.
 */
function LoadingFallback() {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
                backgroundColor: 'rgba(255,255,255,0.4)',
                color: '#64748b'
            }}>
            Loading Portal...
        </div>
    );
}

/**
 * Auth providers wrapper - conditionally wraps children with MSAL providers.
 */
function AppAuthProvider({ children }: { children: ReactNode }) {
    // If auth is disabled, render children directly
    if (isAuthDisabled || !msalInstance) {
        return <>{children}</>;
    }

    // Auth is enabled - wrap with MSAL providers
    return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}

/**
 * Router wrapper - creates and provides the router.
 * Uses useMemo to ensure the router is only created once.
 */
function RouterWrapper() {
    const router = useMemo(() => buildRouter(), []);
    return <RouterProvider router={router} />;
}

/**
 * App Provider - composes all providers for the portal.
 *
 * Includes:
 * - Suspense boundary for lazy loading
 * - Auth providers (MSAL) when auth is enabled
 * - Router (createBrowserRouter with RouterProvider)
 *
 * @example
 * ```tsx
 * <AppProvider />
 * ```
 */
export function AppProvider() {
    return (
        <Suspense fallback={<LoadingFallback />}>
            <AppAuthProvider>
                <RouterWrapper />
            </AppAuthProvider>
        </Suspense>
    );
}
